import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import style from './Pagination.module.scss'

export const PAGE_SIZE = 24

const GAP = 'gap'

// Page buttons as the site draws them: every page up to seven, beyond that the first and the last
// page, a run of five at either end or the current page with its neighbours, and "…" for the rest.
export const pageItems = (page: number, totalPages: number): Array<number | typeof GAP> => {
  const range = (from: number, to: number) => Array.from({ length: to - from + 1 }, (_, i) => from + i)
  if (totalPages <= 7) return range(1, totalPages)
  if (page <= 4) return [...range(1, 5), GAP, totalPages]
  if (page >= totalPages - 3) return [1, GAP, ...range(totalPages - 4, totalPages)]
  return [1, GAP, page - 1, page, page + 1, GAP, totalPages]
}

export const Showing = ({ page, total }: { page: number; total: number }) => {
  const { t } = useSurveyLocale()
  const from = total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0
  const to = Math.min(page * PAGE_SIZE, total)

  return (
    <span
      className={style.showing}
      dangerouslySetInnerHTML={{
        __html: t<(from: number, to: number, total: number) => string>(
          'questions',
          'ec',
          'tesco',
          'showing'
        )(from, to, total)
      }}
    />
  )
}

interface IPaginationProps {
  page: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
  onBackToTop: () => void
}

const Pagination = ({ page, totalPages, total, onPageChange, onBackToTop }: IPaginationProps) => {
  const { t } = useSurveyLocale()

  return (
    <div className={style.pagination} data-testid="SW-EC-Pagination">
      <div className={style.bar}>
        <Showing page={page} total={total} />
        <nav className={style.pages}>
          <button
            type="button"
            className={style.arrow}
            disabled={page <= 1}
            aria-label={t('questions', 'ec', 'tesco', 'previousPage')}
            onClick={() => onPageChange(page - 1)}
          >
            <i className="pi pi-angle-left" />
          </button>
          {pageItems(page, totalPages).map((item, index) =>
            GAP === item ? (
              <span key={`gap-${index}`} className={style.gap}>
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={item === page ? `${style.page} ${style.pageActive}` : style.page}
                aria-current={item === page ? 'page' : undefined}
                onClick={() => item !== page && onPageChange(item)}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            className={style.arrow}
            disabled={page >= totalPages}
            aria-label={t('questions', 'ec', 'tesco', 'nextPage')}
            onClick={() => onPageChange(page + 1)}
          >
            <i className="pi pi-angle-right" />
          </button>
        </nav>
      </div>
      <button
        type="button"
        className={style.backToTop}
        aria-label={t('questions', 'ec', 'tesco', 'backToTop')}
        onClick={onBackToTop}
      >
        <i className="pi pi-arrow-up" />
      </button>
    </div>
  )
}

export default Pagination
